<script setup>
// --------------------------------- Imports -------------------------------- //
import { useIntersectionObserver } from '@vueuse/core';
import { uniq } from 'lodash-es';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';
import { useBiStore } from '~/bi/store/bi.store';

// ---------------------------------- Props --------------------------------- //
const props = defineProps({
  columnConfig: {
    type: Object,
    default: () => ({}),
  },
  componentConfig: {
    type: Object,
    default: () => ({}),
  },
  filterConfig: {
    type: Object,
    default: () => ({}),
  },
  getValuesFromExistingFilters: {
    type: Function,
  },
});
// ---------------------------------- Emits --------------------------------- //

// ---------------------------- Injects/Provides ---------------------------- //

// ----------------------- Variables - Pinia - consts ----------------------- //
const bi_store = useBiStore();
// --------------------- Variables - Pinia - storeToRefs -------------------- //

// ------------------- Variables - Local - consts and lets ------------------ //
const { getMemberDetails } = useBiDataFormatter();
// ------------------------ Variables - Local - refs ------------------------ //
const loadingTrigger = ref(null);
const search = ref('');
const is_column_options_loading = ref(true);
const current_offset = ref(0);
const page_size = ref(1000);
const is_loading_more = ref(false);
const has_more_data = ref(true);
const is_searching = ref(false);
const column_options = ref(uniq([
  ...(props.filterConfig?.operator_value || []),
]));
const is_single_select = computed(() => {
  return props.componentConfig?.single_select || false;
});

// ---------------------- Variables - Local - reactives --------------------- //

// --------------------------- Computed properties -------------------------- //
const checkbox_options = computed(() => {
  const options = column_options.value || [];
  return options;
});

const show_select_all = computed(() => {
  return checkbox_options.value.length > 1;
});
// -------------------------------- Functions ------------------------------- //
function toggleSelectAll(new_val, _old_val, el$) {
  if (new_val) {
    el$.form$.elements$.operator_value.checkAll();
  }
  else {
    el$.form$.elements$.operator_value.uncheckAll();
  }
}

const linked_filters = ref(null);
if (props.getValuesFromExistingFilters)
  linked_filters.value = props.getValuesFromExistingFilters(props);

const is_linked_filters_enabled = ref(linked_filters.value?.has_filters === true);

async function fetchColumnValues(query = {}, append = false) {
  if (append) {
    is_loading_more.value = true;
  }
  else {
    is_column_options_loading.value = true;
    current_offset.value = 0;
  }

  if (is_linked_filters_enabled.value) {
    const values = await linked_filters.value.getValues(query);
    is_column_options_loading.value = false;
    is_loading_more.value = false;
    has_more_data.value = false;
    column_options.value = values;
    return values;
  }

  const cache_key = `${bi_store.widget_builder_config.query.selected_database}-${props.columnConfig.table_name}-${props.columnConfig.name}-q:${query.search.trim()}-l:${page_size.value}-o:${append ? current_offset.value : 0}`;

  let response = bi_store.distinct_column_values_response_cache[cache_key];
  if (!response) {
    response = await bi_store.getColumnValues({
      table_name: encodeURIComponent(props.columnConfig.table_name),
      column_name: encodeURIComponent(props.columnConfig.name),
      query: {
        ...query,
        limit: page_size.value,
        offset: append ? current_offset.value : 0,
      },
    });

    bi_store.distinct_column_values_response_cache[cache_key] = response;
  }

  const { values, pagination } = response;

  if (append) {
    column_options.value = [...column_options.value, ...values];
    // remove duplicate column values from the list
    column_options.value = column_options.value.filter((value, index, self) => self.indexOf(value) === index);

    is_loading_more.value = false;
  }
  else {
    column_options.value = values;
    is_column_options_loading.value = false;
  }

  // Update pagination state based on API response
  has_more_data.value = pagination?.hasMore || false;
  current_offset.value = pagination?.offset + pagination?.currentCount || 0;

  return values;
}

async function loadMoreData() {
  if (is_loading_more.value || !has_more_data.value)
    return;

  await fetchColumnValues({ search: search.value }, true);
}

async function onSearch(val) {
  is_searching.value = true;
  const column_values = await fetchColumnValues({ search: val });
  column_options.value = column_values;
  is_searching.value = false;
}

function onSingleSelectCheckboxChange(new_val, _old_val, el$) {
  if (!Array.isArray(new_val) || new_val.length === 0) {
    el$.update([]);
    return;
  }

  const last = new_val[new_val.length - 1];
  el$.update([last]); // force single‑select behavior
}

// Intersection Observer for infinite scroll
const { stop } = useIntersectionObserver(
  loadingTrigger,
  ([{ isIntersecting }]) => {
    if (isIntersecting && has_more_data.value && !is_loading_more.value) {
      loadMoreData();
    }
  },
  {
    threshold: 0.1,
    rootMargin: '50px',
  },
);

// -------------------------------- Watchers -------------------------------- //

// ----------------------------- Lifecycle Hooks ---------------------------- //
onUnmounted(() => {
  stop();
});
</script>

<template>
  <div>
    <template v-if="!columnConfig.is_table_field">
      <ListElement
        class="max-h-48 scrollbar"
        name="operator_value"
        :controls="{ add: true, remove: true, sort: false }"
        :min="1"
        :initial="1"
        :add-classes="{
          ListElement: {
            add: ['hidden'],
            listItem: ['!flex'],
            remove: ['!ml-0 !flex-none'],
          },
        }"
        :default="filterConfig?.operator_value || [null]"
        :presets="['repeatable_list']"
        @keydown.space.stop
        @keydown.enter.stop
      >
        <template #default="{ index }">
          <TextElement
            class="!grow p-1"
            :name="index"
            :placeholder=" $t('Enter value')"
            v-bind="componentConfig"
            input-type="text"
            autocomplete="off"
          />
        </template>
      </ListElement>
    </template>
    <template v-else>
      <HawkButton
        v-tippy=" is_linked_filters_enabled ? $t('Showing options from filtered results. Click to show all options.') : $t('Showing all options. Click to show only options in current results.')"
        size="xs" type="text" icon :disabled="!getValuesFromExistingFilters" class="!absolute right-4 top-2" @click="is_linked_filters_enabled = !is_linked_filters_enabled;onSearch('')"
      >
        <IconHawkLinkOne v-if="is_linked_filters_enabled" class="text-primary-600 size-4" />
        <IconHawkLinkBrokenOne v-else class="text-gray-600 size-4" />
      </HawkButton>
      <div class="col-span-12">
        <HawkSearchInput
          v-model="search"
          :placeholder="$t('Search')"
          :focus-on-mount="true"
          class="px-2"
          :show_loader="is_searching"
          :additional-classes="{
            ElementLayout: {
              innerWrapper: 'border-x-0 border-b border-t',
            },
            TextElement: {
              inputContainer: 'border-none',
            },
          }"
          :remove-classes="{
            TextElement: {
              inputContainer_default: 'focused:form-ring',
              inputContainer_success: 'focused:form-ring',
            },
          }"
          :icon-class="['!h-4.5', '!w-4.5']"
          :debounce_time="700"
          full_width
          @update:model-value="onSearch"
          @keydown.stop
        />
        <span v-if="column_options?.length === 0 && !is_column_options_loading" class="text-xs text-gray-400 col-span-12 px-2">{{ $t('No results found') }}</span>
      </div>
      <div v-if="is_single_select" class="scrollbar max-h-60 mt-2">
        <CheckboxgroupElement
          rules="required" name="operator_value" :items="checkbox_options" :default="filterConfig?.operator_value?.length ? [filterConfig?.operator_value[0]] : []"
          :add-classes="{
            CheckboxgroupCheckbox: {
              container: 'px-1 py-2 gap-2 hover:bg-gray-50 rounded-lg group/check_item',
              input: 'group-hover/check_item:border-primary-500 !rounded-full checked:!form-bg-icon-radio',
            },
            ElementError: {
              container: 'hidden',
            },
          }"
          :remove-classes="{
            CheckboxgroupCheckbox: {
              input: 'checked:form-bg-icon-check',
            },
          }"
          @change="onSingleSelectCheckboxChange"
        >
          <template #checkbox="{ item, value, el$, classes, isDisabled, id, name }">
            <div class="flex items-center gap-2">
              <input
                :id="id"
                v-model="el$.model"
                type="checkbox"
                :value="value"
                :class="classes.input"
                :name="name"
                :disabled="isDisabled"
                :aria-label="item.label"
              >
              <template v-if="columnConfig.resource_type === 'members'">
                <HawkMembers :members="getMemberDetails(item.value)?.user?.uid || getMemberDetails(item.value)?.team?.uid || item.label" size="xs" type="label" :email_truncate_length="25" :name_truncate_length="25" />
              </template>
              <span
                v-else
                :class="classes.text"
              >
                <HawkText :content="item.label" :length="24" class="whitespace-nowrap" />
              </span>
            </div>
          </template>
        </CheckboxgroupElement>

        <!-- Intersection Observer Trigger -->
        <div v-if="has_more_data" ref="loadingTrigger" class="h-1 w-full" />

        <!-- Loading Indicator -->
        <HawkLoader v-if="is_loading_more" class="col-span-12 !m-2" />
      </div>
      <div v-else class="scrollbar max-h-60 mt-2">
        <CheckboxElement
          v-if="show_select_all" name="select_all" :default="filterConfig?.select_all || false"
          :add-classes="{
            CheckboxElement: {
              wrapper: 'px-1 py-2 gap-2 cursor-pointer hover:bg-gray-50 rounded-lg group',
              input: 'hover:!border-primary-500 ',
            },
          }"
          @change="toggleSelectAll"
        >
          {{ $t('Select all') }}
        </CheckboxElement>
        <CheckboxgroupElement
          rules="required" name="operator_value" :items="checkbox_options" :default="filterConfig?.operator_value || []"
          :add-classes="{
            CheckboxgroupCheckbox: {
              container: 'px-1 py-2 gap-2 hover:bg-gray-50 rounded-lg group/check_item',
              input: 'group-hover/check_item:border-primary-500',
            },
            ElementError: {
              container: 'hidden',
            },
          }"
        >
          <template #checkbox="{ item, value, el$, classes, isDisabled, id, name }">
            <div class="flex items-center gap-2">
              <input
                :id="id"
                v-model="el$.model"
                type="checkbox"
                :value="value"
                :class="classes.input"
                :name="name"
                :disabled="isDisabled"
                :aria-label="item.label"
              >
              <template v-if="columnConfig.resource_type === 'members'">
                <HawkMembers :members="getMemberDetails(item.value)?.user?.uid || getMemberDetails(item.value)?.team?.uid || item.label" size="xs" type="label" :email_truncate_length="25" :name_truncate_length="25" />
              </template>
              <span
                v-else
                :class="classes.text"
              >
                <HawkText :content="item.label" :length="24" class="whitespace-nowrap" />
              </span>
            </div>
          </template>
        </CheckboxgroupElement>

        <!-- Intersection Observer Trigger -->
        <div v-if="has_more_data" ref="loadingTrigger" class="h-1 w-full" />

        <!-- Loading Indicator -->
        <HawkLoader v-if="is_loading_more" class="col-span-12 !m-2" />
      </div>
    </template>
  </div>
</template>
